import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Filter, Search, X } from 'lucide-react';
import { dynamicAPI } from '../services/api';
import { useSiteSettings } from '../hooks/useContent';
import { usePageSeo } from '../hooks/usePageSeo';
import { buildBreadcrumbSchema, buildOrganizationSchema } from '../utils/seo';
import EventCard from '../components/EventCard';
import DualRangeSlider from '../components/DualRangeSlider';
import { filterHeaderSearchItems, upcomingTrekCardMatchesSearch } from '../utils/headerSearchFilter';
import {
  ensureUpcomingSearchIndex,
  getCachedUpcomingSearchItems,
  UPCOMING_SEARCH_FETCH_LIMIT
} from '../utils/upcomingSearchIndex';
import { loadUpcomingTreksBySlugs } from '../utils/loadUpcomingTreksBySlugs';
import { optimizeImageUrl, IMAGE_DISPLAY_WIDTH } from '../utils/optimizeImageUrl.js';
import { durationLabelFromBatch } from '../utils/formatDurationDays';
import {
  ALL_TREK_DIFFICULTY_VALUES,
  difficultyDotTier,
  trekMatchesDifficultySelection
} from '../constants/trekDifficulty';
import './Blogs.css';
import './Events.css';


const AGE_RANGE_MIN = 8;
const AGE_RANGE_MAX = 50;
const DEFAULT_PRICE_MIN = 0;
const DEFAULT_PRICE_MAX = 25000;

const Events = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { settings } = useSiteSettings();
  const publicBase =
    String(settings.site_public_url || '').replace(/\/$/, '') ||
    (typeof window !== 'undefined' ? window.location.origin : '');
  const brand = settings.site_brand_name || 'Invincible NGO';
  const eventsSchema = useMemo(() => {
    const org = buildOrganizationSchema(settings);
    const breadcrumbs = buildBreadcrumbSchema([
      { name: 'Treks & Events', url: '/events' }
    ], publicBase);
    return {
      '@context': 'https://schema.org',
      '@graph': [org, breadcrumbs]
    };
  }, [settings, publicBase]);

  usePageSeo({
    title: `${settings.events_hero_title || 'Treks & Events'} | ${brand}`,
    description:
      String(settings.events_hero_subtitle || '').trim() ||
      settings.site_meta_description,
    canonical: publicBase ? `${publicBase}/events` : undefined,
    schema: eventsSchema
  });

  const [selectedEventTypes, setSelectedEventTypes] = useState([]);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [selectedDifficulties, setSelectedDifficulties] = useState([]);
  const [selectedSeasons, setSelectedSeasons] = useState([]);
  const [sortBy, setSortBy] = useState('popular');
  const [searchQuery, setSearchQuery] = useState('');
  const clientRevealSize = 12;
  const initialApiLimit = 60;
  const apiIncrement = 60;

  const [apiLimit, setApiLimit] = useState(initialApiLimit);
  const [visibleCount, setVisibleCount] = useState(clientRevealSize);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [trekCards, setTrekCards] = useState([]);
  const trekCardsRef = useRef([]);
  trekCardsRef.current = trekCards;
  const [loading, setLoading] = useState(true);
  const [eventTypes, setEventTypes] = useState([]);
  const [regions, setRegions] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [openSections, setOpenSections] = useState({
    sortBy: true,
    eventType: true,
    region: true,
    difficulty: true,
    price: true,
    age: true,
    departure: true,
    season: true
  });
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [eventsNarrowLayout, setEventsNarrowLayout] = useState(false);
  const [eventsSearchSuggestions, setEventsSearchSuggestions] = useState([]);
  const [eventsSearchLoading, setEventsSearchLoading] = useState(false);
  const [eventsShowSearchSuggestions, setEventsShowSearchSuggestions] = useState(false);
  const eventsSearchBlurTimerRef = useRef(null);

  const treksFromUrl = useMemo(() => {
    const raw = String(searchParams.get('treks') || '').trim();
    if (!raw) return [];
    return raw
      .split(',')
      .map((part) => {
        try {
          return decodeURIComponent(part.trim()).toLowerCase();
        } catch {
          return part.trim().toLowerCase();
        }
      })
      .filter(Boolean);
  }, [searchParams]);

  const treksKey = useMemo(() => treksFromUrl.join(','), [treksFromUrl]);

  useEffect(() => {
    const queryFromUrl = String(searchParams.get('search') || '').trim();
    setSearchQuery(queryFromUrl);
  }, [searchParams]);

  /* Same index size as the header so grid results can match top-bar search. */
  useEffect(() => {
    if (searchQuery.trim().length >= 2) {
      setApiLimit((prev) => Math.max(prev, UPCOMING_SEARCH_FETCH_LIMIT));
    }
  }, [searchQuery]);

  useEffect(() => {
    const term = String(searchQuery || '').trim();
    if (term.length < 2) {
      setEventsSearchSuggestions([]);
      setEventsSearchLoading(false);
      return undefined;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        if (!cancelled) setEventsSearchLoading(true);
        await ensureUpcomingSearchIndex(dynamicAPI);
        if (cancelled) return;
        const fresh = getCachedUpcomingSearchItems();
        const filtered = filterHeaderSearchItems(fresh, term).slice(0, 8);
        setEventsSearchSuggestions(
          filtered.map(({ slug, title, location, startDate }) => ({
            slug,
            title,
            location,
            startDate
          }))
        );
      } catch {
        if (!cancelled) setEventsSearchSuggestions([]);
      } finally {
        if (!cancelled) setEventsSearchLoading(false);
      }
    }, 220);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [searchQuery]);

  useEffect(() => {
    setEventsShowSearchSuggestions(false);
  }, [searchParams]);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 860px)');
    const sync = () => setEventsNarrowLayout(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  /* Narrow layout: start with sections collapsed so the page isn’t one tall filter stack */
  useLayoutEffect(() => {
    const mq = window.matchMedia('(max-width: 860px)');
    if (!mq.matches) return;
    setOpenSections({
      sortBy: false,
      eventType: false,
      region: false,
      difficulty: false,
      price: false,
      age: false,
      departure: false,
      season: false
    });
  }, []);

  useEffect(() => {
    if (!mobileFiltersOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setMobileFiltersOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileFiltersOpen]);

  useEffect(() => {
    if (!mobileFiltersOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileFiltersOpen]);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 861px)');
    const onMq = () => {
      if (mq.matches) setMobileFiltersOpen(false);
    };
    mq.addEventListener('change', onMq);
    return () => mq.removeEventListener('change', onMq);
  }, []);

  const loadMoreRef = useRef(null);
  const sortOptions = [
    { value: 'popular', label: 'Most Popular' },
    { value: 'newest', label: 'Newest First' },
    { value: 'price-asc', label: 'Price: Low to High' },
    { value: 'price-desc', label: 'Price: High to Low' },
    { value: 'name', label: 'Name A-Z' }
  ];

  const priceFilterTouched = useRef(false);
  const [priceMin, setPriceMin] = useState(DEFAULT_PRICE_MIN);
  const [priceMax, setPriceMax] = useState(DEFAULT_PRICE_MAX);
  /** Slider track min/max: only ever widens from loaded prices so the bar does not “collapse” when filters narrow results. */
  const [priceTrackEnvelope, setPriceTrackEnvelope] = useState({
    min: DEFAULT_PRICE_MIN,
    max: DEFAULT_PRICE_MAX
  });

  useEffect(() => {
    const prices = trekCards
      .map((c) => Number(c.nextBatch?.price || 0))
      .filter((p) => Number.isFinite(p) && p > 0);
    if (!prices.length) return;
    const bMin = Math.min(...prices);
    const bMax = Math.max(...prices);
    setPriceTrackEnvelope((prev) => ({
      min: Math.min(prev.min, bMin),
      max: Math.max(prev.max, bMax)
    }));
  }, [trekCards]);

  const priceSliderMin = priceTrackEnvelope.min;
  const priceSliderMax =
    priceTrackEnvelope.max <= priceTrackEnvelope.min
      ? priceTrackEnvelope.min + 1
      : priceTrackEnvelope.max;
  const priceLow = Math.max(priceSliderMin, Math.min(priceMin, priceMax, priceSliderMax));
  const priceHigh = Math.max(priceSliderMin, Math.min(Math.max(priceMin, priceMax), priceSliderMax));
  const priceStep = Math.max(
    1,
    Math.min(200, Math.floor((priceSliderMax - priceSliderMin) / 120) || 1)
  );

  const [departureFrom, setDepartureFrom] = useState('');
  const [departureTo, setDepartureTo] = useState('');
  const [ageMin, setAgeMin] = useState(AGE_RANGE_MIN);
  const [ageMax, setAgeMax] = useState(AGE_RANGE_MAX);

  useEffect(() => {
    // Reset infinite-scroll state when filters change (search is client-side only)
    setApiLimit(initialApiLimit);
    setVisibleCount(clientRevealSize);
    setHasMore(true);
    setLoadingMore(false);
  }, [
    sortBy,
    selectedEventTypes,
    selectedRegions,
    selectedDifficulties,
    selectedSeasons,
    priceMin,
    priceMax,
    departureFrom,
    departureTo,
    ageMin,
    ageMax,
    treksFromUrl
  ]);

  useEffect(() => {
    setVisibleCount(clientRevealSize);
  }, [searchQuery]);

  useEffect(() => {
    if (treksFromUrl.length) {
      setVisibleCount(Math.max(clientRevealSize, treksFromUrl.length));
    }
  }, [treksFromUrl, trekCards.length, clientRevealSize]);

  useEffect(() => {
    if (!treksFromUrl.length) return;
    setSelectedEventTypes([]);
    setSelectedRegions([]);
    setSelectedDifficulties([]);
    setSelectedSeasons([]);
  }, [treksKey]);

  useEffect(() => {
    if (!treksFromUrl.length) return undefined;

    let cancelled = false;
    const applyRowsToSidebar = (rows) => {
      const types = [
        ...new Set(
          rows
            .flatMap((r) => {
              const names = r?.nextBatch?.eventTypeNames;
              if (Array.isArray(names) && names.length) return names;
              const one = r?.nextBatch?.eventTypeName;
              return one ? [one] : [];
            })
            .filter(Boolean)
        )
      ];
      const regs = [...new Set(rows.map((r) => r?.trek?.location).filter(Boolean))];
      const seasonsFromUpcoming = [...new Set(rows.map((r) => r?.trek?.season).filter(Boolean))];
      const defaultSeasons = ['Spring', 'Summer', 'Monsoon', 'Autumn', 'Winter'];
      setEventTypes(types);
      setRegions(regs);
      setSeasons([...new Set([...seasonsFromUpcoming, ...defaultSeasons])]);
    };

    const loadFooterTreks = async () => {
      if (!trekCardsRef.current.length) setLoading(true);
      try {
        const rows = await loadUpcomingTreksBySlugs(treksFromUrl);
        if (cancelled) return;
        setTrekCards(rows);
        applyRowsToSidebar(rows);
        setHasMore(false);
        setVisibleCount(Math.max(clientRevealSize, rows.length, treksFromUrl.length));
      } catch {
        if (!cancelled) {
          setTrekCards([]);
          setHasMore(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadFooterTreks();
    return () => {
      cancelled = true;
    };
  }, [treksKey, clientRevealSize, treksFromUrl]);

  useEffect(() => {
    if (treksFromUrl.length) return undefined;

    let cancelled = false;
    const loadEvents = async () => {
      const hadListContent = trekCardsRef.current.length > 0;
      if (apiLimit === initialApiLimit) {
        if (!hadListContent) setLoading(true);
      } else {
        setLoadingMore(true);
      }
      try {
        const params = {
          limit: apiLimit
        };
        if (sortBy === 'newest') params.sort = 'startDate:desc';
        if (sortBy === 'popular') params.sort = 'popularity:desc';
        if (sortBy === 'name') params.sort = 'name:asc';
        if (sortBy === 'price-asc') params.sort = 'price:asc';
        if (sortBy === 'price-desc') params.sort = 'price:desc';

        {
          const lo = Math.min(priceMin, priceMax);
          const hi = Math.max(priceMin, priceMax);
          const isDefaultPriceSpan =
            lo === DEFAULT_PRICE_MIN && hi === DEFAULT_PRICE_MAX;
          if (
            !isDefaultPriceSpan &&
            Number.isFinite(lo) &&
            Number.isFinite(hi) &&
            hi > 0
          ) {
            params.minPrice = lo;
            params.maxPrice = hi;
          }

          const dFrom = String(departureFrom || '').trim();
          const dTo = String(departureTo || '').trim();
          if (/^\d{4}-\d{2}-\d{2}$/.test(dFrom) && /^\d{4}-\d{2}-\d{2}$/.test(dTo)) {
            params.departureFrom = dFrom;
            params.departureTo = dTo;
          }

          const aLo = Math.min(ageMin, ageMax);
          const aHi = Math.max(ageMin, ageMax);
          if (
            aLo >= 0 &&
            aHi >= 0 &&
            aLo <= aHi &&
            !(aLo === AGE_RANGE_MIN && aHi === AGE_RANGE_MAX)
          ) {
            params.participantAgeMin = aLo;
            params.participantAgeMax = aHi;
          }
        }

        const response = await dynamicAPI.getUpcomingTreks(params);
        if (cancelled) return;
        const rows = response.data?.data || [];
        setTrekCards(rows);

        // Make sidebar options reflect only upcoming event batches.
        // (Derived from the same API response that powers the events list.)
        const types = [
          ...new Set(
            rows.flatMap((r) => {
              const names = r?.nextBatch?.eventTypeNames;
              if (Array.isArray(names) && names.length) return names;
              const one = r?.nextBatch?.eventTypeName;
              return one ? [one] : [];
            }).filter(Boolean)
          )
        ];
        const regs = [...new Set(rows.map((r) => r?.trek?.location).filter(Boolean))];
        const seasonsFromUpcoming = [...new Set(rows.map((r) => r?.trek?.season).filter(Boolean))];
        const defaultSeasons = ['Spring', 'Summer', 'Monsoon', 'Autumn', 'Winter'];

        setEventTypes(types);
        setRegions(regs);
        setSeasons([...new Set([...seasonsFromUpcoming, ...defaultSeasons])]);

        setHasMore(rows.length === apiLimit);
      } catch {
        if (!cancelled) {
          setTrekCards([]);
          setHasMore(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
        if (!cancelled && apiLimit !== initialApiLimit) setLoadingMore(false);
      }
    };
    loadEvents();
    return () => { cancelled = true; };
  }, [apiLimit, sortBy, priceMin, priceMax, departureFrom, departureTo, ageMin, ageMax, treksKey]);

  const toggleFilter = (value, selectedValues, setter) => {
    if (selectedValues.includes(value)) {
      setter(selectedValues.filter((v) => v !== value));
    } else {
      setter([...selectedValues, value]);
    }
  };

  const filteredGroups = useMemo(() => {
    return trekCards.filter((card) => {
      const trek = card.trek;
      const cardSlug = String(trek?.slug || '').trim().toLowerCase();
      const matchTreks = !treksFromUrl.length || (cardSlug && treksFromUrl.includes(cardSlug));
      const matchSearch = upcomingTrekCardMatchesSearch(card, searchQuery);

      if (treksFromUrl.length) {
        return matchTreks && matchSearch;
      }

      const eventTypeLabelList = (() => {
        const names = card?.nextBatch?.eventTypeNames;
        if (Array.isArray(names) && names.length) return names;
        const one = card?.nextBatch?.eventTypeName;
        return one ? [one] : [];
      })();
      const matchEventType =
        !selectedEventTypes.length || eventTypeLabelList.some((n) => selectedEventTypes.includes(n));
      const matchRegion = !selectedRegions.length || selectedRegions.includes(trek?.location);
      const matchDifficulty = trekMatchesDifficultySelection(trek?.difficulty, selectedDifficulties);
      const matchSeason = !selectedSeasons.length || selectedSeasons.includes(trek?.season);
      const lo = Math.min(priceMin, priceMax);
      const hi = Math.max(priceMin, priceMax);
      const isDefaultPriceSpan = lo === DEFAULT_PRICE_MIN && hi === DEFAULT_PRICE_MAX;
      const cardPrice = Number(card.nextBatch?.price ?? 0);
      const matchPrice =
        isDefaultPriceSpan ||
        (Number.isFinite(cardPrice) && cardPrice > 0 && cardPrice >= lo && cardPrice <= hi);
      return (
        matchEventType &&
        matchRegion &&
        matchDifficulty &&
        matchSeason &&
        matchSearch &&
        matchTreks &&
        matchPrice
      );
    });
  }, [
    trekCards,
    selectedEventTypes,
    selectedRegions,
    selectedDifficulties,
    selectedSeasons,
    searchQuery,
    treksFromUrl,
    priceMin,
    priceMax
  ]);

  const sortedGroups = useMemo(() => {
    const list = [...filteredGroups];
    if (sortBy === 'newest') {
      list.sort((a, b) => new Date(b.nextBatch?.startDate || 0) - new Date(a.nextBatch?.startDate || 0));
    }
    if (sortBy === 'popular') {
      list.sort(
        (a, b) =>
          (b.trek?.detailClickCount || 0) - (a.trek?.detailClickCount || 0) ||
          new Date(b.nextBatch?.startDate || 0) - new Date(a.nextBatch?.startDate || 0)
      );
    }
    if (sortBy === 'name') list.sort((a, b) => (a.trek?.name || '').localeCompare(b.trek?.name || ''));
    if (sortBy === 'price-asc') list.sort((a, b) => (a.nextBatch?.price || 0) - (b.nextBatch?.price || 0));
    if (sortBy === 'price-desc') list.sort((a, b) => (b.nextBatch?.price || 0) - (a.nextBatch?.price || 0));
    return list;
  }, [filteredGroups, sortBy]);

  const totalGroups = sortedGroups.length;
  const visibleGroups = useMemo(() => sortedGroups.slice(0, visibleCount), [sortedGroups, visibleCount]);


  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry?.isIntersecting) return;

        if (loading || loadingMore) return;

        // If we still have hidden (already fetched) results, reveal them first.
        if (visibleCount < totalGroups) {
          setVisibleCount((prev) => Math.min(prev + clientRevealSize, totalGroups));
          return;
        }

        // Otherwise, fetch the next larger batch.
        if (hasMore) {
          setApiLimit((prev) => prev + apiIncrement);
          setVisibleCount((prev) => prev + clientRevealSize);
        }
      },
      { root: null, rootMargin: '1200px 0px', threshold: 0.01 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [apiIncrement, clientRevealSize, hasMore, loading, loadingMore, totalGroups, visibleCount]);

  const resetFilters = () => {
    setSelectedEventTypes([]);
    setSelectedRegions([]);
    setSelectedDifficulties([]);
    setSelectedSeasons([]);
    setSortBy('popular');
    setSearchQuery('');
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('search');
        next.delete('treks');
        return next;
      },
      { replace: true }
    );
    priceFilterTouched.current = false;
    setPriceTrackEnvelope({ min: DEFAULT_PRICE_MIN, max: DEFAULT_PRICE_MAX });
    setPriceMin(DEFAULT_PRICE_MIN);
    setPriceMax(DEFAULT_PRICE_MAX);
    setDepartureFrom('');
    setDepartureTo('');
    setAgeMin(AGE_RANGE_MIN);
    setAgeMax(AGE_RANGE_MAX);
  };

  const toggleSectionOpen = (key) => {
    setOpenSections((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const renderDifficultyFilterGroup = () => (
    <div className="events-filter-group">
      <button
        type="button"
        className="events-filter-toggle"
        onClick={() => toggleSectionOpen('difficulty')}
      >
        <span>Difficulty</span>
        <span
          className={`events-filter-toggle-icon ${openSections.difficulty ? 'open' : ''}`}
        >
          ‹
        </span>
      </button>
      {openSections.difficulty && (
        <>
          <div className="events-filter-options events-filter-options-difficulty">
              {ALL_TREK_DIFFICULTY_VALUES.map((opt) => {
                const tier = difficultyDotTier(opt);
                return (
                  <label key={opt} className="events-check events-check-difficulty">
                    <input
                      type="checkbox"
                      checked={selectedDifficulties.includes(opt)}
                      onChange={() => toggleFilter(opt, selectedDifficulties, setSelectedDifficulties)}
                    />
                    <span className="events-difficulty-filter-row">
                      <span className="events-difficulty-filter-label">{opt}</span>
                      <span className="events-difficulty-filter-bars" aria-hidden>
                        <span className={tier >= 1 ? 'active' : ''} />
                        <span className={tier >= 2 ? 'active' : ''} />
                        <span className={tier >= 3 ? 'active' : ''} />
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
        </>
      )}
    </div>
  );

  const renderFilterGroup = (key, title, options, selectedValues, setter) => (
    <div className="events-filter-group">
      <button
        type="button"
        className="events-filter-toggle"
        onClick={() => toggleSectionOpen(key)}
      >
        <span>{title}</span>
        <span
          className={`events-filter-toggle-icon ${
            openSections[key] ? 'open' : ''
          }`}
        >
          ‹
        </span>
      </button>
      {openSections[key] && (
        <>
          {options.length === 0 ? (
            <p className="events-filter-empty">No options</p>
          ) : (
            <div className="events-filter-options">
              {options.map((opt) => (
                <label key={opt} className="events-check">
                  <input
                    type="checkbox"
                    checked={selectedValues.includes(opt)}
                    onChange={() => toggleFilter(opt, selectedValues, setter)}
                  />
                  <span>{opt}</span>
                </label>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );

  const formatInr = (value) => `Rs.${Number(value || 0).toLocaleString('en-IN')}`;

  const commitEventsSearchToUrl = (rawValue) => {
    const term = String(rawValue ?? '').trim();
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (term) {
          next.set('search', term);
          next.delete('treks');
        } else {
          next.delete('search');
        }
        return next;
      },
      { replace: true }
    );
    setEventsShowSearchSuggestions(false);
  };

  const openEventSearchSuggestion = (item) => {
    if (!item?.slug) return;
    setEventsShowSearchSuggestions(false);
    const url = item.startDate
      ? `/events/${item.slug}?date=${encodeURIComponent(item.startDate)}`
      : `/events/${item.slug}`;
    navigate(url);
  };

  const eventsHeroTitle =
    settings.events_hero_title || settings.events_page_title || 'Discover Your Next Adventure';
  const eventsHeroSubtitle =
    settings.events_hero_subtitle ||
    settings.events_page_subtitle ||
    'Browse through our curated list of treks and expeditions.';
  const eventsHeroImage = String(settings.events_hero_image || '').trim();
  const eventsHeroClassName = eventsHeroImage
    ? 'blog-hero-banner blog-hero-banner-with-image'
    : 'blog-hero-banner';

  return (
    <div className="events-page">
      <div
        className={eventsHeroClassName}
        style={
          eventsHeroImage
            ? {
                backgroundImage: `linear-gradient(135deg, rgba(15,23,42,0.25) 0%, rgba(15,23,42,0.25) 40%, rgba(15,23,42,0.25) 100%), url(${eventsHeroImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }
            : undefined
        }
      >
        <div className="blog-hero-inner events-hero-inner">
          <div className="events-hero-top">
            <div className="events-hero-titles">
              <h1 className="blog-hero-title">{eventsHeroTitle}</h1>
              <p className="blog-hero-subtitle">{eventsHeroSubtitle}</p>
            </div>
          </div>
        </div>
      </div>

      {mobileFiltersOpen && (
        <div
          className="events-sidebar-backdrop"
          aria-hidden="true"
          onClick={() => setMobileFiltersOpen(false)}
        />
      )}

      <div className="events-layout">
        <aside
          id="events-filters-panel"
          className={`events-sidebar${mobileFiltersOpen ? ' events-sidebar--open' : ''}`}
          aria-hidden={eventsNarrowLayout && !mobileFiltersOpen ? true : undefined}
          {...(eventsNarrowLayout && mobileFiltersOpen
            ? { role: 'dialog', 'aria-modal': true, 'aria-labelledby': 'events-filters-title' }
            : {})}
        >
          <div className="events-sidebar-head">
            <h3 id="events-filters-title">
              <Filter size={16} aria-hidden />
              Filters
            </h3>
            <div className="events-sidebar-head-actions">
              <button type="button" className="events-sidebar-reset" onClick={resetFilters}>Reset</button>
              <button
                type="button"
                className="events-sidebar-close modal-close-circle"
                aria-label="Close filters"
                onClick={() => setMobileFiltersOpen(false)}
              >
                <X size={20} strokeWidth={2.25} />
              </button>
            </div>
          </div>
          <div className="events-filter-group">
            <button
              type="button"
              className="events-filter-toggle"
              onClick={() => toggleSectionOpen('sortBy')}
            >
              <span>Sort By</span>
              <span
                className={`events-filter-toggle-icon ${
                  openSections.sortBy ? 'open' : ''
                }`}
              >
                ‹
              </span>
            </button>
            {openSections.sortBy && (
              <div className="events-filter-options events-sort-options">
                {sortOptions.map((opt) => (
                  <label key={opt.value} className="events-check">
                    <input
                      type="radio"
                      name="events-sort-by"
                      checked={sortBy === opt.value}
                      onChange={() => setSortBy(opt.value)}
                    />
                    <span>{opt.label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {renderFilterGroup('eventType', 'Event Type', eventTypes, selectedEventTypes, setSelectedEventTypes)}
          {renderFilterGroup('region', 'Region', regions, selectedRegions, setSelectedRegions)}
          {renderDifficultyFilterGroup()}

          <div className="events-filter-group">
            <button
              type="button"
              className="events-filter-toggle"
              onClick={() => toggleSectionOpen('price')}
            >
              <span>Price Range</span>
              <span
                className={`events-filter-toggle-icon ${
                  openSections.price ? 'open' : ''
                }`}
              >
                ‹
              </span>
            </button>
            {openSections.price && (
              <div className="events-filter-price-body events-filter-dual-range">
                <DualRangeSlider
                  min={priceSliderMin}
                  max={priceSliderMax}
                  low={priceLow}
                  high={priceHigh}
                  step={priceStep}
                  ariaLabel="Price range in rupees"
                  onChange={({ low: lo, high: hi }) => {
                    priceFilterTouched.current = true;
                    setPriceMin(lo);
                    setPriceMax(hi);
                  }}
                />
                <div className="events-price-labels events-price-labels-dual events-price-labels--combined">
                  <span>
                    {formatInr(priceLow)} – {formatInr(priceHigh)}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="events-filter-group">
            <button
              type="button"
              className="events-filter-toggle"
              onClick={() => toggleSectionOpen('age')}
            >
              <span>Traveller age</span>
              <span className={`events-filter-toggle-icon ${openSections.age ? 'open' : ''}`}>‹</span>
            </button>
            {openSections.age && (
              <div className="events-filter-price-body events-filter-dual-range">
                <DualRangeSlider
                  min={AGE_RANGE_MIN}
                  max={AGE_RANGE_MAX}
                  low={Math.min(ageMin, ageMax)}
                  high={Math.max(ageMin, ageMax)}
                  step={1}
                  ariaLabel="Traveller age range"
                  onChange={({ low: lo, high: hi }) => {
                    setAgeMin(lo);
                    setAgeMax(hi);
                  }}
                />
                <div className="events-price-labels events-price-labels-dual events-price-labels--combined">
                  <span>
                    {Math.min(ageMin, ageMax)} – {Math.max(ageMin, ageMax)} yrs
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="events-filter-group">
            <button
              type="button"
              className="events-filter-toggle"
              onClick={() => toggleSectionOpen('departure')}
            >
              <span>Departure window</span>
              <span className={`events-filter-toggle-icon ${openSections.departure ? 'open' : ''}`}>‹</span>
            </button>
            {openSections.departure && (
              <div className="events-filter-departure-body">
                <label className="events-date-label">From</label>
                <input
                  type="date"
                  className="events-date-input"
                  value={departureFrom}
                  onChange={(e) => setDepartureFrom(e.target.value)}
                />
                <label className="events-date-label">To</label>
                <input
                  type="date"
                  className="events-date-input"
                  value={departureTo}
                  onChange={(e) => setDepartureTo(e.target.value)}
                />
              </div>
            )}
          </div>

          {renderFilterGroup('season', 'Season', seasons, selectedSeasons, setSelectedSeasons)}
        </aside>

        <div className="events-right">
        <div className="events-toolbar">
          <form
            className="events-search-form"
            onSubmit={(e) => {
              e.preventDefault();
              commitEventsSearchToUrl(searchQuery);
            }}
          >
            <span className="events-search-submit" aria-hidden="true">
              <Search size={18} strokeWidth={2} aria-hidden />
            </span>
            <div className="events-search-field-wrap">
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search Adventures, Camps, Offers..."
                enterKeyHint="search"
                autoComplete="off"
                onFocus={() => setEventsShowSearchSuggestions(true)}
                onBlur={() => {
                  if (eventsSearchBlurTimerRef.current) {
                    window.clearTimeout(eventsSearchBlurTimerRef.current);
                  }
                  eventsSearchBlurTimerRef.current = window.setTimeout(
                    () => setEventsShowSearchSuggestions(false),
                    120
                  );
                }}
              />
              {eventsShowSearchSuggestions && String(searchQuery || '').trim().length >= 2 && (
                <div className="search-suggestions" role="listbox" aria-label="Search suggestions">
                  {eventsSearchLoading ? (
                    <div className="search-suggestion-empty">Searching...</div>
                  ) : eventsSearchSuggestions.length > 0 ? (
                    eventsSearchSuggestions.map((item) => (
                      <button
                        key={item.slug}
                        type="button"
                        className="search-suggestion-item"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => openEventSearchSuggestion(item)}
                      >
                        <span className="search-suggestion-title">{item.title}</span>
                        {item.location ? (
                          <span className="search-suggestion-meta">{item.location}</span>
                        ) : null}
                      </button>
                    ))
                  ) : (
                    <button
                      type="button"
                      className="search-suggestion-item"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => commitEventsSearchToUrl(searchQuery)}
                    >
                      <span className="search-suggestion-title">
                        Search for &quot;{String(searchQuery || '').trim()}&quot;
                      </span>
                      <span className="search-suggestion-meta">View all matching events</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          </form>
            <button
              type="button"
              className="events-filter-open-btn"
              aria-label="Open filters"
              aria-expanded={mobileFiltersOpen}
              aria-controls="events-filters-panel"
              onClick={() => setMobileFiltersOpen((open) => !open)}
            >
              <Filter size={22} strokeWidth={2} aria-hidden />
            </button>
          </div>

          <section className="events-main">
          {loading ? (
            <div className="events-grid events-grid-loading">
              {Array.from({ length: clientRevealSize }).map((_, idx) => (
                <article key={idx} className="events-card events-card-skeleton">
                  <div className="events-card-image skeleton-block" />
                  <div className="events-card-body">
                    <div className="skeleton-line skeleton-line-short" />
                    <div className="skeleton-line" />
                    <div className="skeleton-line" />
                  </div>
                </article>
              ))}
            </div>
          ) : totalGroups === 0 ? (
            <div className="events-empty">
              {treksFromUrl.length
                ? 'No upcoming events for these treks right now. Check back soon or browse all events.'
                : 'No events found. Try changing filters.'}
            </div>
          ) : (
            <>
              <div className="events-grid">
                {visibleGroups.map((card) => {
                  const trek = card.trek;
                  const selectedBatch = card.nextBatch;
                  const key = trek?.id || selectedBatch?.id;
                  const price = Number(selectedBatch?.price || 0);
                  const slug = trek?.slug || trek?.id || selectedBatch?.id;
                  const imageUrl = optimizeImageUrl(
                    trek?.media?.[0]?.url || 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80',
                    { width: IMAGE_DISPLAY_WIDTH.card }
                  );
                  const durationLabel =
                    durationLabelFromBatch(selectedBatch?.startDate, selectedBatch?.endDate) ||
                    (selectedBatch?.startDate || '');
                  const unifiedEvent = {
                    id: key,
                    batchId: selectedBatch?.id,
                    trekId: trek?.id,
                    slug,
                    title: trek?.name || selectedBatch?.code || 'Event',
                    imageUrl,
                    media: Array.isArray(trek?.media) ? trek.media : undefined,
                    // Match Home cards & other sections: tagline first, then description, then any precomputed shortDescription
                    shortDescription: trek?.tagline || trek?.description || trek?.shortDescription || '',
                    duration: durationLabel,
                    startDate: selectedBatch?.startDate,
                    location: trek?.location,
                    difficulty: trek?.difficulty,
                    price,
                    brochureUrl: trek?.brochureUrl || null,
                    // All upcoming departure dates for this trek (backend populated)
                    departureDates: Array.isArray(card.departureDates) ? card.departureDates : undefined
                  };
                  return (
                    <EventCard key={key} event={unifiedEvent} />
                  );
                })}
              </div>

              {loadingMore && (
                <div className="events-grid events-grid-loading">
                  {Array.from({ length: clientRevealSize }).map((_, idx) => (
                    <article key={`loading-more-${idx}`} className="events-card events-card-skeleton">
                      <div className="events-card-image skeleton-block" />
                      <div className="events-card-body">
                        <div className="skeleton-line skeleton-line-short" />
                        <div className="skeleton-line" />
                        <div className="skeleton-line" />
                      </div>
                    </article>
                  ))}
                </div>
              )}

              {(hasMore || visibleCount < totalGroups) && (
                <div ref={loadMoreRef} className="events-infinite-sentinel" />
              )}
            </>
          )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default Events;
